import { AxiosInstance } from "axios";
import TokenHelper from "../utils/tokenHelper";

export const storySubjectType = "volunteerexperience";

export const basePath = "/volunteer-experiences";

// All the paths for this api domain.
export const storyPaths = {
  getById: (id: number) => basePath + `/${id}`,
  toggleStoryLike: () => "/luvit",
  deleteStory: (id: number) => `${basePath}/${id}/report-abuse`,
  reportStory: (id: number) => `${basePath}/${id}/report-abuse`,
  createStory: () => basePath,
  updateStory: (id: number) => `${basePath}/${id}`,
};

// Request parameters for the createStory and updateStory endpoints.
export interface StoryParams {
  description?: string;
  guest?: {
    firstName: string;
    lastName: string;
    email: string;
    zip?: string;
  };
  organization?: {
    id: number;
  };
  visibilityStatus?: number;
  minutes?: string;
  images?: {
    imageId: string;
    imageVersion: string;
    meta: {
      format: string;
      height: number;
      size: number;
      src: string;
      width: string;
    };
    sort: number;
    status: string;
  }[];
}

class StoryApi {
  api: AxiosInstance;
  tokenHelper: TokenHelper;

  constructor(api: AxiosInstance, tokenHelper: TokenHelper) {
    this.api = api;
    this.tokenHelper = tokenHelper;
  }

  // Get story by id.
  // GET /volunteer-experiences/<id>
  getById = (id: number) => {
    let headers: Record<string, any> = {};
    if (process?.browser) {
      const token = this.tokenHelper.getJwtToken();

      if (token) {
        headers["Authorization"] = this.tokenHelper.formatAuthHeader(token);
      }
    }

    return this.api.get(storyPaths.getById(id), {
      headers,
    });
  };

  // Toggle comment like.
  // POST /luvit
  toggleStoryLike = (storyId: number) => {
    const token = this.tokenHelper.getRefreshToken();
    return this.api.post(
      storyPaths.toggleStoryLike(),
      {
        subjectType: storySubjectType,
        subjectId: storyId,
      },
      {
        headers: {
          Authorization: this.tokenHelper.formatAuthHeader(token),
        },
      }
    );
  };

  // Report a story for abuse.
  // POST /volunteer-experiences/:id/report-abuse
  reportStory = (storyId: number) => {
    const token = this.tokenHelper.getJwtToken();
    return this.api.post(storyPaths.deleteStory(storyId), {
      headers: {
        Authorization: this.tokenHelper.formatAuthHeader(token),
      },
    });
  };

  // Delete a story.
  // This uses the same endpoint as report abuse 😬. Creating a
  // separate function as this functionality should diverge at some point 🤞.
  // POST /volunteer-experiences/:id/report-abuse
  deleteStory = (storyId: number) => {
    const token = this.tokenHelper.getJwtToken();
    return this.api.post(storyPaths.deleteStory(storyId), {
      headers: {
        Authorization: this.tokenHelper.formatAuthHeader(token),
      },
    });
  };

  /**
   * POST /volunteer-experiences
   * Purpose:
   *  - Create a story
   *  - Right now we only are accounting for creating a community board story,
   *  - TODO: Add params needed to the StoryParams interface in order to create a story for an event/collection. (Refer to "create" method in volunteer experiences controller in the weserve codebase for logic and params needed.)
   *      - JIRA Ticket: https://castnet.atlassian.net/browse/ACQR-418 (events)
   */
  createStory = (createStoryParams: StoryParams) => {
    const token = this.tokenHelper.getJwtToken();

    let headers: Record<string, any> = {};

    /**
     * We need to append the JWT to the auth header if the story was shared by a user who was auth'd into the app.
     * If the guest object is present, that means an unauthenticated user is sharing a story. The backend will handle
     * the creation of the user for this story so no authorization token is needed.
     */
    if (!createStoryParams.guest && token) {
      headers.Authorization = this.tokenHelper.formatAuthHeader(token);
    }

    return this.api.post(storyPaths.createStory(), createStoryParams, {
      headers,
    });
  };

  /**
   * PATCH /volunteer-experiences/:id
   * Purpose:
   *  - Update a specific story's data
   */
  updateStory = ({
    storyId,
    updateStoryParams,
    guestJWT,
  }: {
    storyId: number;
    updateStoryParams: StoryParams;
    guestJWT?: string;
  }) => {
    // Check if there was a guest JWT passed into this method, otherwise check for the token in local storage.
    const token = guestJWT || this.tokenHelper.getJwtToken();

    return this.api.patch(storyPaths.updateStory(storyId), updateStoryParams, {
      headers: {
        Authorization: this.tokenHelper.formatAuthHeader(token),
      },
    });
  };
}

export default StoryApi;
